import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';
import {
  installationMetadataSchema,
  type InstallationMetadata,
  type InstallationMetadataPatch,
} from '@/schemas/installations';

async function fetchInstallation(id: string): Promise<InstallationMetadata> {
  const resp = await apiFetch(`/installations/${id}`);
  const json = await resp.json();
  return installationMetadataSchema.parse(json);
}

async function patchInstallation(
  id: string,
  patch: InstallationMetadataPatch
): Promise<InstallationMetadata> {
  const resp = await apiFetch(`/installations/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const json = await resp.json();
  return installationMetadataSchema.parse(json);
}

interface InstallationMetadataDialogProps {
  /** Target seat (`activations.id`); `null` keeps the dialog closed. */
  activationId: string | null;
  /** Licence the seat belongs to — the query key refreshed on save. */
  licenseId: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Admin-only editor for a seat's `label`/`notes`/`location`. The current values are
 * read with `GET /api/installations/:id` when the dialog opens and written back with
 * a partial `PATCH`, so clearing a field stores `null` (never an empty string).
 */
export default function InstallationMetadataDialog({
  activationId,
  licenseId,
  onOpenChange,
}: InstallationMetadataDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState('');

  const detailQuery = useQuery({
    queryKey: ['installation', activationId],
    queryFn: () => fetchInstallation(activationId!),
    enabled: activationId !== null,
    staleTime: 0,
    retry: false,
  });

  // Prefill the form whenever a (new) seat is loaded.
  useEffect(() => {
    const data = detailQuery.data;
    if (!data) return;
    setLabel(data.label ?? '');
    setNotes(data.notes ?? '');
    setLocation(data.location ?? '');
  }, [detailQuery.data]);

  const mutation = useMutation({
    mutationFn: (patch: InstallationMetadataPatch) => patchInstallation(activationId!, patch),
    onSuccess: () => {
      toast.success(t('licenses.detail.devices.metadata.toastSaved'));
      queryClient.invalidateQueries({ queryKey: ['license', 'devices', licenseId] });
      // The fleet table renders label/location/notes from this list — refetch so a
      // freshly saved robot shows up immediately, without a manual reload.
      queryClient.invalidateQueries({ queryKey: ['license', 'installation-metadata', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['installation', activationId] });
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('licenses.detail.devices.metadata.toastError'));
    },
  });

  const toNullable = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };

  const handleSave = () => {
    mutation.mutate({
      label: toNullable(label),
      notes: toNullable(notes),
      location: toNullable(location),
    });
  };

  const busy = mutation.isPending || detailQuery.isLoading;

  return (
    <Dialog open={activationId !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('licenses.detail.devices.metadata.title')}</DialogTitle>
          <DialogDescription>{t('licenses.detail.devices.metadata.description')}</DialogDescription>
        </DialogHeader>

        {detailQuery.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {t('licenses.detail.devices.metadata.loadError')}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="installation-label">
                {t('licenses.detail.devices.metadata.label')}
              </Label>
              <Input
                id="installation-label"
                autoComplete="off"
                maxLength={200}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="installation-label"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="installation-location">
                {t('licenses.detail.devices.metadata.location')}
              </Label>
              <Input
                id="installation-location"
                autoComplete="off"
                maxLength={200}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                data-testid="installation-location"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="installation-notes">
                {t('licenses.detail.devices.metadata.notes')}
              </Label>
              <textarea
                id="installation-notes"
                rows={3}
                maxLength={5000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex min-h-[84px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="installation-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={busy}
            data-testid="installation-metadata-save"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('licenses.detail.devices.metadata.saving')}
              </>
            ) : (
              t('licenses.detail.devices.metadata.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
